import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { tagKeys } from "../../../../../api/queryKeys";
import ChipTag from "../../../../../components/ChipTag";
import CustomToolTip from "../../../../../components/CustomTooltip";
import TagChipWithDropDown from "../../components/TagsDropDown/TagChipWithDropDown";
import MoreTagsChip from "./MoreTagsChip";

const TagCell = ({ entity }) => {
  const cellRef = useRef(null);
  const hiddenRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);
  const [widthsOfChips, setWidthsOfChips] = useState([]);
  const { getAllCustomTags } = useAPI();

  const GAPS = 8;
  const MORE_TAGS_CHIP_WIDTH = 45;

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === cellRef.current) {
          const newWidth = entry.contentRect.width;
          setCellWidth(newWidth);
        }
      });
    });

    if (cellRef.current) {
      resizeObserver.observe(cellRef.current);
    }

    if (hiddenRef.current) {
      const widthsOfChips = Array.from(hiddenRef.current.children).map(
        (child) => child.offsetWidth
      );
      setWidthsOfChips(widthsOfChips);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [entity?.custom_tags]);

  const calculateTagsToShowCount = () => {
    if (!entity?.custom_tags || entity?.custom_tags?.length === 0) return 0;
    if (entity?.custom_tags?.length === 1) return 1;

    let tagsToShowCount = entity?.custom_tags?.length;
    let totalWidth =
      GAPS * (tagsToShowCount - 1) +
      widthsOfChips.reduce((acc, width) => acc + width, 0);
    if (totalWidth <= cellWidth) return tagsToShowCount;

    while (totalWidth > cellWidth) {
      tagsToShowCount--;
      totalWidth =
        GAPS * tagsToShowCount +
        widthsOfChips
          .slice(0, tagsToShowCount)
          .reduce((acc, width) => acc + width, 0) +
        MORE_TAGS_CHIP_WIDTH;
      if (totalWidth <= cellWidth) {
        return tagsToShowCount;
      }
      if (tagsToShowCount === 1) {
        return 1;
      }
    }
  };

  const tagsToShowCount = calculateTagsToShowCount();

  const { data: allTags } = useQuery({
    queryKey: tagKeys.all,
    queryFn: getAllCustomTags,
    select: (data) => data.data,
  });

  const tagIdToKeyValueMap = {};
  allTags
    ?.filter((tag) => !tag.is_deleted)
    ?.forEach((tag) => {
      tagIdToKeyValueMap[tag.tag_id] = {
        key: tag.key,
        value: tag.value,
      };
    });

  const validTags =
    entity?.custom_tags
      ?.map((customTag) => tagIdToKeyValueMap[customTag])
      ?.filter((tagKeyValue) => tagKeyValue?.key !== undefined) || [];

  if (!validTags || validTags?.length === 0) return null;

  return (
    <>
      <Box
        ref={hiddenRef}
        display="flex"
        position="absolute"
        visibility="hidden"
        height={0}
      >
        {validTags?.map((tag, index) => (
          <ChipTag key={index} tagKey={tag.key} tagValue={tag.value} />
        ))}
      </Box>
      <Box
        ref={cellRef}
        width="100%"
        display="flex"
        alignItems="center"
        maxWidth="100%"
        gap={`${GAPS}px`}
        height="36px"
        padding="6px 8px 6px 8px"
      >
        {validTags?.slice(0, tagsToShowCount)?.map((tag, index) => (
          <TagChipWithDropDown
            key={index}
            tagKey={tag.key}
            tagValue={tag.value}
            entities={[entity]}
          />
        ))}
        {tagsToShowCount < validTags?.length && (
          <CustomToolTip
            title={validTags
              ?.slice(tagsToShowCount)
              ?.map((tag) => `${tag.key}: ${tag.value}`)
              .join(", ")}
            placement="top"
          >
            <Box>
              <MoreTagsChip
                entity={entity}
                tags={validTags?.slice(tagsToShowCount)}
                width={MORE_TAGS_CHIP_WIDTH}
              />
            </Box>
          </CustomToolTip>
        )}
      </Box>
    </>
  );
};

export default TagCell;
